/**
 * Composition wrapper that hands auth state to the (pure) shell layout.
 *
 * `AppShellLayout` lives in `components/layout/` and is therefore
 * forbidden from importing `features/auth`. This thin wrapper sits in
 * the composition root (`app/`) where cross-feature imports are
 * allowed, pulls auth data, and forwards everything as props.
 */
import { useNavigate } from 'react-router';

import { AppShellLayout } from '@/components/layout/app-shell-layout/app-shell-layout';
import { paths } from '@/config/paths';
import { signOut } from '@/features/auth/api/amplify-auth';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import {
  usePlans,
  useSubscription,
  type Plan,
  type SubscriptionStatus,
} from '@/features/billing/api/billing-api';
import { useSetupStatus } from '@/features/onboarding/api/setup-api';

/**
 * Plan line for the sidebar account chip. Derived from the live
 * subscription (the chip is presentational and cannot read features), so a
 * real subscriber sees their plan, trial state, and remaining credits
 * instead of a hardcoded "Free Plan".
 */
function planLabelFor(
  sub: SubscriptionStatus | undefined,
  plans: Plan[] | undefined,
): string | undefined {
  if (!sub || !sub.has_subscription || !sub.plan_slug) return undefined; // → "Free Plan"
  const name = plans?.find((p) => p.slug === sub.plan_slug)?.name ?? sub.plan_slug;
  // Surface the running credit balance in the chip (the "out of credits"
  // gate lands on a charge, so the live remaining count is useful at a glance).
  const remaining = sub.credits?.monthly_remaining;
  const credits = remaining !== undefined ? ` · ${Math.floor(remaining).toLocaleString()} cr` : '';
  if (sub.is_trialing) return `${name} (Trial)${credits}`;
  if (sub.status === 'past_due') return `${name} (Past Due)${credits}`;
  return `${name} Plan${credits}`;
}

export function AppShellRoute() {
  const { user } = useAuth();
  const reset = useAuthStore((s) => s.reset);
  const navigate = useNavigate();
  const subQuery = useSubscription();
  const plansQuery = usePlans();
  // Setup progress drives the persistent "Get Started" entry in the
  // sidebar (with its progress ring). Undefined until it resolves, and
  // null on error, so the entry simply stays hidden rather than flashing.
  const { data: setup } = useSetupStatus();
  const setupProgress = setup ? { done: setup.done_count, total: setup.total } : null;

  const handleSignOut = () => {
    void (async () => {
      await signOut();
      reset();
      void navigate(paths.home, { replace: true });
    })();
  };

  return (
    <AppShellLayout
      user={user}
      onSignOut={handleSignOut}
      setupProgress={setupProgress}
      planLabel={planLabelFor(subQuery.data, plansQuery.data)}
    />
  );
}

export default AppShellRoute;
